import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Row } from "antd";
import {
  CombustionManualFuelVolume,
  ManualFuelVolume,
  MasterSettings,
  MonthlyVolume,
  StaticColumnsType,
} from "src/entity/manual-fuel-volume/manual-fuel-volume.entity";
import FormItem from "antd/es/form/FormItem";
import { ReportStatusEnum } from "src/entity/report-voc-emission/report-voc-emission.entity";
import { Masters } from "src/entity/fuel-volume/fuel-volume.entity";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { formatNumber } from "src/utils/process-number";

const ManualFuelVolumeModal = (props: {
  open: boolean;
  initialData: CombustionManualFuelVolume;
  loading: boolean;
  onCancelManualFuelVolume: () => void;
  onSaveManFeulVol: (data: CombustionManualFuelVolume) => void;
}) => {
  const { open, initialData, loading, onCancelManualFuelVolume, onSaveManFeulVol } = props;
  const [form] = Form.useForm();
  const [combustionData, setCombustionData] = useState<CombustionManualFuelVolume>(initialData);
  const [unitVolume, setUnitVolume] = useState<string>();

  // Update combustionData when initialData prop changes
  useEffect(() => {
    setCombustionData(initialData);
  }, [initialData]);

  const staticColumnsKey: StaticColumnsType[] = [
    {
      label: "Stack ID",
      key: "stackId",
    },
    {
      label: "Source ID",
      key: "sourceId",
    },
    {
      label: "Area",
      key: "area",
    },
    {
      label: "Fuel Name",
      key: "fuelName",
    },
  ];

  type MonthManual = keyof ManualFuelVolume;
  const months: MonthManual[] = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

  const handleGetMaster = () => {
    let optionUnitVolumePI = Masters.find((el) => el.label === "unitVolume");
    let optionUnit = optionUnitVolumePI?.options.find((el) => el.key === combustionData.fuelType);
    if (optionUnit) {
      const valueUnit = optionUnit.value;
      if (valueUnit) {
        setUnitVolume(valueUnit);
        setCombustionData((prevState) => {
          return {
            ...prevState,
            manualFuelVolume: {
              ...prevState.manualFuelVolume,
              unit: valueUnit,
            },
          };
        });
      }
    }
  };

  const handleSaveCombustion = async () => {
    try {
      await form.validateFields();
      await onSaveManFeulVol(combustionData);
    } catch (error) {
      console.log("Failed:", error);
    }
  };

  useEffect(() => {
    form.setFieldsValue(combustionData);
    handleGetMaster();
  }, []);

  useEffect(() => {
    form.setFieldsValue(combustionData);
  }, [combustionData, form]);

  const handleInputChange = (value: number | null, month: keyof ManualFuelVolume) => {
    if (month != "unit") {
      setCombustionData((prevData) => ({
        ...prevData,
        manualFuelVolume: {
          ...prevData.manualFuelVolume,
          [month]: {
            ...prevData.manualFuelVolume[month],
            value: value,
          },
        },
      }));
    }
  };

  return (
    <>
      {open && (
        <Modal
          title={<ModalTitle title="Fuel Volume" />}
          open={open}
          width={1000}
          okText="Save"
          onCancel={onCancelManualFuelVolume}
          cancelText="Cancel"
          centered
          bodyStyle={{ height: 350, overflowY: "auto", overflowX: "hidden" }}
          maskClosable={false}
          footer={
            <Row className="w-full border-t" justify={"end"}>
              <Button className="mt-4" key="submit" type="primary" onClick={handleSaveCombustion} loading={loading}>
                Save
              </Button>
            </Row>
          }
        >
          <Form size="large" layout="vertical" autoComplete="off" form={form}>
            <Row gutter={16}>
              {staticColumnsKey.map((column, index) => (
                <Col xs={{ span: 24 }} sm={{ span: 5 }} md={{ span: 5 }} key={index}>
                  <FormItem label={column.label}>
                    <Input disabled value={String(combustionData[column.key])} />
                  </FormItem>
                </Col>
              ))}
              <Col xs={{ span: 24 }} sm={{ span: 4 }} md={{ span: 4 }} key={"unit"}>
                <FormItem label="Unit">
                  <Input disabled value={unitVolume} />
                </FormItem>
              </Col>
            </Row>
            <Row gutter={16}>
              {months.map((month, index) => (
                <Col xs={{ span: 24 }} sm={{ span: 6 }} md={{ span: 4 }} key={index}>
                  <Form.Item
                    label={month.charAt(0).toUpperCase() + month.slice(1)}
                    rules={[{ required: true, message: "Please input a number!" }]}
                  >
                    <InputNumber
                      min={0}
                      step={1}
                      style={{ width: "100%" }}
                      value={month != "unit" ? (combustionData.manualFuelVolume[month].value ?? 0) : 0}
                      disabled={(combustionData.manualFuelVolume[month] as MonthlyVolume).reportStatus !== ReportStatusEnum.PROCESSING}
                      onChange={(value) => handleInputChange(value, month)}
                      onKeyDown={(e) => {
                        onKeyDownNumberWithFixedDecimal(e, 2);
                      }}
                      formatter={(value) => formatNumber(value, 2)}
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default ManualFuelVolumeModal;
